import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Typography, TextField, IconButton, InputAdornment, Divider, Button,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DropdownTextField from '../form-components/DropdownTextField';
import MediaPreviewOverlay from '../common/MediaPreviewOverlay';
import UrlStatusIcon from '../common/UrlStatusIcon';
import LocalText from '../common/LocalText';
import { useLocalText } from '../common/useLocalText';
import useUrlValidation from '../../hooks/useUrlValidation';
import { positionIsComplete, positionIsPartial } from '../portals/PortalShared';
import { AD_PORTAL_POSITIONS, AD_PORTAL_POSITIONS_IN_DISPLAY_ORDER } from '../../constants/portalPositions';
import { URL_MAX_LENGTH, adPortalIssues, emptyAdPosition } from '../../utils/adPortal';
import { isHttpsUrl, isValidUrl } from '../../utils/validators';

const Label = ({ children, required }) => (
  <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: '#5383b3', fontSize: 10, mb: 0.5 }}>
    <LocalText>{children}</LocalText>
    {required && <span style={{ color: '#d32f2f', marginLeft: 3 }}>*</span>}
  </Typography>
);

// state -> dot colour and the words a screen reader gets (the dot alone is not enough)
const TAB_STATE = {
  invalid:  { dot: 'error.main',   border: 'error.main', text: 'has an invalid URL' },
  partial:  { dot: '#ed6c02',      border: '#ed6c02',    text: 'is incomplete' },
  complete: { dot: 'success.main', border: 'primary.lighter', text: 'is complete' },
  empty:    { dot: '#b0bec5',      border: 'primary.lighter', text: 'is empty' },
};

/**
 * The portal of a 3D ad: a localized title and up to four positions (Left, Center, Right, Back), each a link (https only) with a
 * localized thumbnail. There is no main media field: the portal's main picture is the preview image of the selected 3D model (the
 * ad form fills it in when it saves). Like the booth form: a position with only a link or only a thumbnail is
 * "partial" and blocks saving until it is completed or cleared; an empty position is simply not sent.
 *
 * value             the editor state (see utils/adPortal.js)
 * onChange(next)    receives the whole new state
 * onValidityChange  called with true/false whenever the portal becomes valid/invalid (an empty portal is valid: it is optional)
 * Re-mount it (change its `key`) when the state is replaced from outside, so the per-position async results start fresh.
 */
const AdPortalEditor = ({ value, onChange, disabled = false, onValidityChange }) => {
  const [active, setActive] = useState(0);
  const [linkPreviewOpen, setLinkPreviewOpen] = useState(false);
  // async reachability results, kept per position across tab switches (same approach as the booth portal form)
  const [positionValidation, setPositionValidation] = useState(() => AD_PORTAL_POSITIONS.map(() => ({ linkOk: true, thumbOk: true })));

  const activeRef = useRef(active);
  activeRef.current = active;

  const position = value.positions[active] ?? emptyAdPosition();
  const issues = adPortalIssues(value);

  const update = (patch) => onChange({ ...value, ...patch });
  const updatePosition = (patch) =>
    onChange({ ...value, positions: value.positions.map((p, i) => (i === active ? { ...p, ...patch } : p)) });

  // a view-only editor does not probe the links it shows (the other URL fields skip validation the same way)
  const { status: linkStatus, reason: linkReason } = useUrlValidation(disabled ? '' : position.portalLink, 'url');

  // the active position's link result is persisted; only 'idle' (empty) and 'valid' pass, 'checking' blocks too
  useEffect(() => {
    setPositionValidation((pv) => pv.map((v, i) => (i === active ? { ...v, linkOk: linkStatus === 'valid' || linkStatus === 'idle' } : v)));
  }, [linkStatus, active]);

  const handleThumbnailValidation = useCallback((allValid) => {
    setPositionValidation((pv) => pv.map((v, i) => (i === activeRef.current ? { ...v, thumbOk: allValid } : v)));
  }, []);

  const asyncOk = value.positions.every((p, i) => {
    const pv = positionValidation[i];
    const hasLink = (p.portalLink ?? '').trim().length > 0;
    return !(hasLink && pv?.linkOk === false) && pv?.thumbOk !== false;
  });
  const valid = issues.ok && asyncOk;

  useEffect(() => {
    if (onValidityChange) onValidityChange(valid);
  }, [valid, onValidityChange]);

  const stateOf = (pos, i) => {
    const hasLink = (pos.portalLink ?? '').trim().length > 0;
    const unreachable = (hasLink && positionValidation[i]?.linkOk === false) || positionValidation[i]?.thumbOk === false;
    if (issues.badLinks.includes(i) || issues.badThumbnails.includes(i) || issues.longPositions.includes(i) || unreachable) return 'invalid';
    if (positionIsPartial(pos)) return 'partial';
    if (positionIsComplete(pos)) return 'complete';
    return 'empty';
  };

  const linkPlaceholder = useLocalText('https://...');
  const linkHelper = (() => {
    const link = (position.portalLink ?? '').trim();
    if (!link) return positionIsPartial(position) ? 'Required — thumbnail already set for this position' : '';
    if (!isHttpsUrl(link)) return isValidUrl(link) ? 'The link must start with https://' : 'The link must be a valid https:// URL';
    if (link.length > URL_MAX_LENGTH) return `The link can be at most ${URL_MAX_LENGTH} characters`;
    if (linkStatus === 'checking') return 'Checking…';
    if (linkStatus === 'invalid') return linkReason === 'unreachable' ? 'URL could not be reached' : 'The URL is not reachable or has an invalid format';
    if (linkStatus === 'valid') return 'Valid';
    return '';
  })();
  const linkInvalid = Boolean((position.portalLink ?? '').trim())
    && (!isHttpsUrl(position.portalLink) || (position.portalLink ?? '').trim().length > URL_MAX_LENGTH || linkStatus === 'invalid');

  const completeCount = value.positions.filter(positionIsComplete).length;

  return (
    <Box data-testid="ad-portal-editor">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {/* Title */}
        <Label required={issues.hasLinks && !disabled}>Portal Title</Label>
        <DropdownTextField
          label="Portal Title"
          values={value.title}
          attribute="value"
          onChange={(title) => update({ title })}
          disabled={disabled}
          required={issues.hasLinks && !disabled}
        />

        <Divider sx={{ my: 1 }} />

        {/* Position tabs */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <Label>Positions</Label>
          <Typography variant="caption" data-testid="ad-portal-count" sx={{ color: '#85a5c6', fontSize: 11 }}>
            {`${completeCount}/${AD_PORTAL_POSITIONS.length} `}<LocalText>positions ready</LocalText>
          </Typography>
        </Box>
        <Box role="tablist" aria-label="Portal positions" sx={{ display: 'flex', gap: 1 }}>
          {/* shown as Left, Right, Center, Back; each tab still works on its own position id (0, 2, 1, 3) */}
          {AD_PORTAL_POSITIONS_IN_DISPLAY_ORDER.map(({ id, name }) => {
            const isActive = active === id;
            const state = stateOf(value.positions[id], id);
            const look = TAB_STATE[state];
            return (
              <Button
                key={id}
                role="tab"
                aria-selected={isActive}
                aria-label={`${name} ${look.text}`}
                data-testid={`ad-portal-tab-${id}`}
                data-state={state}
                onClick={() => setActive(id)}
                size="small"
                sx={{
                  flex: 1, borderRadius: 2, textTransform: 'none', fontWeight: isActive ? 700 : 500, position: 'relative', py: 0.75,
                  bgcolor: isActive ? 'primary.main' : 'transparent',
                  color: isActive ? 'white' : 'primary.main',
                  border: '2px solid',
                  borderColor: isActive ? 'primary.main' : look.border,
                  '&:hover': { bgcolor: isActive ? 'primary.dark' : 'rgba(10,50,109,0.06)' },
                }}
              >
                <LocalText>{name}</LocalText>
                <Box sx={{ position: 'absolute', top: 5, right: 6, width: 7, height: 7, borderRadius: '50%', bgcolor: look.dot }} />
              </Button>
            );
          })}
        </Box>

        {issues.hasContent && !issues.hasLinks && (
          <Typography variant="caption" data-testid="ad-portal-hint" sx={{ color: '#b45309', fontSize: 11, mt: 0.5 }}>
            <LocalText>A portal is only saved with at least one position that has both a link and a thumbnail.</LocalText>
          </Typography>
        )}

        {/* The active position: re-mounted on switch so the DropdownTextField state starts clean */}
        <Box key={active} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
          <Label required={positionIsPartial(position) && !disabled}>Portal Link</Label>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
            <TextField
              fullWidth
              size="small"
              placeholder={linkPlaceholder}
              value={position.portalLink}
              onChange={(e) => updatePosition({ portalLink: e.target.value })}
              disabled={disabled}
              error={!disabled && linkInvalid}
              helperText={disabled ? '' : linkHelper}
              inputProps={{ 'aria-label': 'Portal link', 'data-testid': 'ad-portal-link' }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><LinkIcon sx={{ fontSize: 16, color: 'text.secondary' }} /></InputAdornment>,
                endAdornment: disabled ? undefined : <InputAdornment position="end"><UrlStatusIcon status={linkStatus} reason={linkReason} /></InputAdornment>,
              }}
            />
            <IconButton
              size="small"
              aria-label="Preview portal link"
              disabled={!position.portalLink}
              onClick={(e) => { e.currentTarget.blur(); setLinkPreviewOpen(true); }}
              sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Box>

          <Label required={positionIsPartial(position) && !disabled}>Thumbnail</Label>
          <DropdownTextField
            label="Thumbnail Image URL"
            values={position.thumbnailLocalizations}
            attribute="mediaLink"
            onChange={(thumbnailLocalizations) => updatePosition({ thumbnailLocalizations })}
            disabled={disabled}
            previewable={!disabled}
            mediaType="image"
            urlValidationType={disabled ? null : 'image'}
            onValidationChange={disabled ? undefined : handleThumbnailValidation}
            required={positionIsPartial(position) && !disabled}
            maxLength={URL_MAX_LENGTH}
          />
        </Box>
      </Box>

      <MediaPreviewOverlay open={linkPreviewOpen} onClose={() => setLinkPreviewOpen(false)} type="link" url={position.portalLink} />
    </Box>
  );
};

export default AdPortalEditor;
